import React, { useState, useEffect, useLayoutEffect } from 'react';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import VideoPlayer from '../components/ui/video-player';
import { bestWorkProjects, videoGallery } from '../mock';
import { ArrowLeft, X, ChevronLeft, ChevronRight } from 'lucide-react';

const BestWork = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [featuredIndex, setFeaturedIndex] = useState(0);

  const featuredProject = bestWorkProjects[featuredIndex];

  const goToFeaturedPrevious = () => {
    setFeaturedIndex(index =>
      index === 0 ? bestWorkProjects.length - 1 : index - 1
    );
  };

  const goToFeaturedNext = () => {
    setFeaturedIndex(index =>
      index === bestWorkProjects.length - 1 ? 0 : index + 1
    );
  };

  const selectProjectById = projectId => {
    const id = Number(projectId);
    const project = bestWorkProjects.find(p => p.id === id);

    if (!project) {
      setSelectedProject(null);
      setLightboxImage(null);
      return;
    }

    setSelectedProject(project);
    setLightboxImage(null);
    setCurrentImageIndex(0);
    setImageLoading(false);
  };

  const openProject = project => {
    setSelectedProject(project);
    setLightboxImage(null);
    setCurrentImageIndex(0);
    setImageLoading(false);
    // Push state to browser history for proper back button handling
    window.history.pushState(
      { projectId: project.id },
      '',
      `#project-${project.id}`
    );
  };

  const closeProject = () => {
    setSelectedProject(null);
    setLightboxImage(null);
    setCurrentImageIndex(0);
    setImageLoading(false);
    // Update URL to remove hash when going back to main view
    window.history.replaceState(null, '', window.location.pathname);
  };

  const openLightbox = (image, index) => {
    setLightboxImage(image);
    setCurrentImageIndex(index);
    setImageLoading(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    document.body.style.overflow = 'auto';
  };

  const goToPrevious = () => {
    if (!selectedProject) return;
    setImageLoading(true);
    const currentGallery = selectedProject.gallery;
    const previousIndex =
      currentImageIndex > 0 ? currentImageIndex - 1 : currentGallery.length - 1;
    setCurrentImageIndex(previousIndex);
    setTimeout(() => {
      setLightboxImage(currentGallery[previousIndex]);
      setImageLoading(false);
    }, 150);
  };

  const goToNext = () => {
    if (!selectedProject) return;
    setImageLoading(true);
    const currentGallery = selectedProject.gallery;
    const nextIndex =
      currentImageIndex < currentGallery.length - 1 ? currentImageIndex + 1 : 0;
    setCurrentImageIndex(nextIndex);
    setTimeout(() => {
      setLightboxImage(currentGallery[nextIndex]);
      setImageLoading(false);
    }, 150);
  };

  const goToImage = index => {
    setImageLoading(true);
    setCurrentImageIndex(index);
    setTimeout(() => {
      setLightboxImage(selectedProject.gallery[index]);
      setImageLoading(false);
    }, 150);
  };

  const handleKeyPress = e => {
    if (!lightboxImage) return;

    switch (e.key) {
      case 'ArrowLeft':
        goToPrevious();
        break;
      case 'ArrowRight':
        goToNext();
        break;
      case 'Escape':
        closeLightbox();
        break;
      default:
        break;
    }
  };

  useLayoutEffect(() => {
    const handleProjectHash = () => {
      const hash = window.location.hash;
      if (hash.startsWith('#project-')) {
        const projectId = Number(hash.replace('#project-', ''));
        selectProjectById(projectId);
        return;
      }

      setSelectedProject(null);
      setLightboxImage(null);
      setCurrentImageIndex(0);
      setImageLoading(false);
    };

    handleProjectHash();

    const handlePopState = event => {
      if (!event.state) {
        setSelectedProject(null);
        setLightboxImage(null);
        setCurrentImageIndex(0);
        setImageLoading(false);
        return;
      }

      if (event.state.projectId) {
        selectProjectById(event.state.projectId);
      } else {
        setSelectedProject(null);
        setLightboxImage(null);
        setCurrentImageIndex(0);
        setImageLoading(false);
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    if (!selectedProject) return;

    selectedProject.gallery.forEach(image => {
      const img = new Image();
      img.src = image;
    });
  }, [selectedProject]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [lightboxImage, currentImageIndex, selectedProject]);

  if (selectedProject) {
    return (
      <>
        <SEO
          title={`${selectedProject.coupleName} - Best Work | Frames by Gaurav`}
          description={`${selectedProject.description} View our featured ${selectedProject.event} photography project from ${selectedProject.date}.`}
          keywords={`wedding photography project, ${selectedProject.event} photography, best wedding photographer lucknow, frames by gaurav portfolio`}
          canonicalUrl="/best-work"
        />
        <Layout>
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <button
                onClick={closeProject}
                className="flex items-center text-gray-600 hover:text-black mb-8 transition-colors bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg"
              >
                <ArrowLeft className="mr-2" size={20} />
                Back to Our Best Work
              </button>

              <div className="mb-12">
                <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
                  {selectedProject.coupleName}
                </h1>
                <div className="flex flex-wrap gap-4 text-gray-600">
                  <span className="text-lg">{selectedProject.event}</span>
                  <span className="text-lg">•</span>
                  <span className="text-lg">{selectedProject.date}</span>
                </div>
                <p className="text-xl text-gray-700 mt-4">
                  {selectedProject.description}
                </p>
              </div>

              {/* Project Gallery */}
              <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-6 space-y-6">
                {selectedProject.gallery.map((image, index) => (
                  <button
                    key={index}
                    type="button"
                    className="group relative overflow-hidden break-inside-avoid cursor-pointer rounded-lg shadow-lg hover:shadow-2xl transition-all duration-500 w-full text-left"
                    onClick={() => openLightbox(image, index)}
                    aria-label={`View ${selectedProject.coupleName} photo ${index + 1}`}
                  >
                    <img
                      src={image}
                      alt={`${selectedProject.coupleName} - ${index + 1}`}
                      className="w-full h-auto transform group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-6">
                      <p className="text-white text-xs uppercase tracking-widest mb-2 font-semibold">
                        {selectedProject.coupleName}
                      </p>
                      <h3 className="text-white text-xl font-bold transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                        Photo {index + 1} of {selectedProject.gallery.length}
                      </h3>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Lightbox */}
          {lightboxImage && (
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Photo preview"
              className="fixed inset-0 z-[100] bg-black/95"
            >
              {/* Close Button */}
              <button
                type="button"
                onClick={closeLightbox}
                className="absolute right-4 top-4 z-20 rounded-full bg-white/10 p-3 text-white backdrop-blur-sm transition-colors hover:bg-white/20 hover:text-gray-300 md:right-6 md:top-6"
                aria-label="Close photo preview"
              >
                <X size={26} />
              </button>

              {/* Previous Button */}
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                className="absolute left-3 top-1/2 z-20 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20 hover:text-gray-300 md:left-6 md:h-14 md:w-14"
                aria-label="Previous photo"
              >
                <ChevronLeft size={28} />
              </button>

              {/* Next Button */}
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  goToNext();
                }}
                className="absolute right-3 top-1/2 z-20 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20 hover:text-gray-300 md:right-6 md:h-14 md:w-14"
                aria-label="Next photo"
              >
                <ChevronRight size={28} />
              </button>

              {/* Photo Counter */}
              <div className="absolute left-4 top-4 z-20 rounded-full bg-black/70 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm md:left-6 md:top-6">
                {currentImageIndex + 1} / {selectedProject.gallery.length}
              </div>

              {/* Largest available image area, keeping the full photo in frame */}
              <div
                className="absolute inset-x-14 bottom-28 top-16 flex items-center justify-center md:inset-x-24 md:bottom-32 md:top-20"
                role="presentation"
              >
                <img
                  src={lightboxImage}
                  alt={`${selectedProject.coupleName} - ${currentImageIndex + 1}`}
                  className={`h-auto w-auto max-h-full max-w-full object-contain transition-opacity duration-300 ${
                    imageLoading ? 'opacity-0' : 'opacity-100'
                  }`}
                  onLoad={() => setImageLoading(false)}
                />
                {imageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>

              {/* Thumbnail Slider at Bottom */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/95 to-transparent px-4 pb-4 pt-8">
                <div className="max-w-7xl mx-auto">
                  <div className="flex items-center gap-3">
                    {/* Scroll Left Button */}
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        document.getElementById('thumbnail-slider')?.scrollBy({
                          left: -200,
                          behavior: 'smooth',
                        });
                      }}
                      className="flex-shrink-0 rounded-full bg-white/10 p-2.5 text-white backdrop-blur-sm transition-colors hover:bg-white/20 hover:text-gray-300"
                      aria-label="Scroll thumbnails left"
                    >
                      <ChevronLeft size={24} />
                    </button>

                    {/* Thumbnail Strip */}
                    <div
                      id="thumbnail-slider"
                      aria-label="Photo thumbnails"
                      className="flex flex-1 gap-3 overflow-x-auto py-2 scrollbar-hide"
                      style={{
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                      }}
                    >
                      {selectedProject.gallery.map((image, index) => (
                        <button
                          key={index}
                          onClick={e => {
                            e.stopPropagation();
                            goToImage(index);
                          }}
                          type="button"
                          className={`h-14 w-16 flex-shrink-0 overflow-hidden rounded-md transition-all duration-300 md:h-16 md:w-20 ${
                            index === currentImageIndex
                              ? 'ring-2 ring-white opacity-100'
                              : 'opacity-50 hover:opacity-100 hover:scale-105'
                          }`}
                          aria-label={`Go to photo ${index + 1}`}
                          aria-current={
                            index === currentImageIndex ? 'true' : undefined
                          }
                        >
                          <img
                            src={image}
                            alt={`${selectedProject.coupleName} - ${index + 1}`}
                            className="w-24 h-24 object-cover"
                          />
                        </button>
                      ))}
                    </div>

                    {/* Scroll Right Button */}
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        document.getElementById('thumbnail-slider')?.scrollBy({
                          left: 200,
                          behavior: 'smooth',
                        });
                      }}
                      className="flex-shrink-0 rounded-full bg-white/10 p-2.5 text-white backdrop-blur-sm transition-colors hover:bg-white/20 hover:text-gray-300"
                      aria-label="Scroll thumbnails right"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Layout>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Our Best Work - Featured Photography Projects | Frames by Gaurav"
        description="Explore our finest photography projects from weddings and special events in Lucknow. View beautifully captured moments and stories that showcase our expertise in professional photography."
        keywords="best wedding photography lucknow, featured wedding projects, professional photography portfolio, wedding photography showcase, frames by gaurav best work"
        canonicalUrl="/best-work"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Best Work - Frames by Gaurav',
          description:
            'Featured projects showcasing our finest photography and the beautiful stories we&apos;ve captured.',
          url: 'https://www.framesbygaurav.com/best-work',
        }}
      />
      <Layout>
        <main className="bg-white">
          <section className="pt-12 pb-8 md:pt-16">
            <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-end justify-between gap-5">
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-gray-500 mb-3">
                  Frames by Gaurav · Lucknow
                </p>
                <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-black">
                  Selected Stories
                </h1>
              </div>
              <p className="max-w-sm text-gray-600 text-base md:text-lg">
                Weddings, people and celebrations, remembered in photographs.
              </p>
            </div>
          </section>

          <section
            aria-label="Featured photography stories"
            className="pb-16 md:pb-24"
          >
            <div className="relative h-[65vh] min-h-[440px] max-h-[780px] overflow-hidden bg-neutral-900">
              <img
                key={featuredProject.coverImage}
                src={featuredProject.coverImage}
                alt={`${featuredProject.coupleName}, ${featuredProject.event}`}
                className="absolute inset-0 h-full w-full object-cover animate-[fadeInUp_500ms_ease-out]"
                fetchPriority="high"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-black/20" />

              <div className="absolute inset-x-0 bottom-0 px-6 pb-8 md:px-14 md:pb-12 text-white">
                <p className="text-xs uppercase tracking-[0.2em] text-white/75 mb-3">
                  {featuredProject.event} <span className="px-2">/</span>{' '}
                  {featuredProject.date}
                </p>
                <h2 className="text-4xl md:text-6xl font-bold mb-6">
                  {featuredProject.coupleName}
                </h2>
                <div className="flex flex-wrap items-center justify-between gap-5">
                  <button
                    type="button"
                    onClick={() => openProject(featuredProject)}
                    className="border border-white/80 px-6 py-3 text-xs uppercase tracking-[0.16em] hover:bg-white hover:text-black transition-colors"
                  >
                    View story
                  </button>
                  <div className="flex items-center gap-3">
                    <span className="min-w-16 text-right text-sm tabular-nums">
                      {String(featuredIndex + 1).padStart(2, '0')} /{' '}
                      {String(bestWorkProjects.length).padStart(2, '0')}
                    </span>
                    <button
                      type="button"
                      onClick={goToFeaturedPrevious}
                      className="grid h-11 w-11 place-items-center border border-white/50 hover:bg-white hover:text-black transition-colors"
                      aria-label="Previous featured story"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      type="button"
                      onClick={goToFeaturedNext}
                      className="grid h-11 w-11 place-items-center border border-white/50 hover:bg-white hover:text-black transition-colors"
                      aria-label="Next featured story"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="pb-20 md:pb-28">
            <div className="max-w-7xl mx-auto px-6">
              <div className="flex items-end justify-between gap-6 mb-8">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-2">
                    The portfolio
                  </p>
                  <h2 className="text-3xl md:text-4xl font-bold">
                    More stories
                  </h2>
                </div>
                <span className="text-sm text-gray-500">
                  {bestWorkProjects.length} projects
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-10 md:gap-x-7 md:gap-y-14">
                {bestWorkProjects.map((project, index) => (
                  <button
                    key={project.id}
                    type="button"
                    onClick={() => openProject(project)}
                    aria-label={`View ${project.coupleName} project`}
                    className="group text-left"
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                      <img
                        src={project.coverImage}
                        alt={`${project.coupleName}, ${project.event}`}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="flex items-start justify-between gap-4 pt-4">
                      <div>
                        <h3 className="text-xl md:text-2xl font-bold text-black group-hover:underline underline-offset-4">
                          {project.coupleName}
                        </h3>
                        <p className="mt-1 text-sm text-gray-600">
                          {project.event} · {project.date}
                        </p>
                      </div>
                      <span className="pt-1 text-xs text-gray-500 tabular-nums">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </section>
        </main>

        {/* Video Highlights Section */}
        {videoGallery && videoGallery.length > 0 && (
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <h2 className="text-4xl md:text-5xl font-bold text-center mb-8 tracking-tight">
                Watch Our Cine Frames!
              </h2>
              <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
                Short highlights and teasers from recent shoots — click to play.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {videoGallery.map(video => {
                  const src = video.youtubeId
                    ? `https://www.youtube.com/watch?v=${String(video.youtubeId).split('&')[0]}`
                    : video.src || video.url;
                  return (
                    <div key={video.id} className="group">
                      <VideoPlayer
                        src={src}
                        thumbnail={video.thumbnail}
                        title={video.title}
                      />
                      <h3 className="mt-3 font-semibold text-lg">
                        {video.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {video.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </Layout>
    </>
  );
};

export default BestWork;
